import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { RefundService } from '../../features/refund/services/refund.service';
import { PaymentMethodsEnum } from '../../../assets/enum/paymentMethodsEnum';
import { InvoiceService } from '../../features/invoice/services/invoice.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

@Component({
  selector: 'app-refund-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    FormsModule,
    MatDatepickerModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './refund-new.component.html',
  styleUrl: './refund-new.component.scss',
})
export class RefundNewComponent {
  refundForm: FormGroup;
  refundData: any = {};
  refundId: any;
  public paymentMethodData: any[] = [];
  public preferenceData: any[] = [];
  today: Date = new Date();
  accountsData: any;
  paidInvoiceData: any;
  selectedCustomer: string = 'Customer';
  selectedCustomerId: any;
  maxRefundAmount: number = 0;
  customers: any[] = [];
  private searchSubject = new Subject<string>();
  private invoiceSearchSubject = new Subject<string>();
  maxRefund = false;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private refundService: RefundService,
    private invoiceService: InvoiceService
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadPaymentMethods();
    this.fetchAccounts();
    this.getCustomers();
    this.route.paramMap.subscribe((params) => {
      this.refundId = params.get('id');
      if (this.refundId) {
        this.fetchrefundById();
      }
    });
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getCustomers(searchTerm);
      });
    this.invoiceSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchPaidInvoicesData(this.selectedCustomerId, searchTerm);
      });

    this.refundForm.get('customerId')?.valueChanges.subscribe((customerId) => {
      if (customerId) {
        this.maxRefund = false;
        this.selectedCustomerId = customerId;
        this.fetchPaidInvoicesData(customerId, '');
        this.searchSubject.next('');
      }
    });

    this.refundForm.get('paymentMethod')?.valueChanges.subscribe((method) => {
      if (method === 'Cheque') {
        this.refundForm.get('chequeNo')?.setValidators([Validators.required]);
        this.refundForm.get('chequeDate')?.setValidators([Validators.required]);
      } else {
        this.refundForm.get('chequeNo')?.clearValidators();
        this.refundForm.get('chequeDate')?.clearValidators();
      }
      this.refundForm.get('chequeNo')?.updateValueAndValidity();
      this.refundForm.get('chequeDate')?.updateValueAndValidity();
    });

    this.refundForm.get('invoiceId')?.valueChanges.subscribe((invoiceId) => {
      const selectedInvoice = this.paidInvoiceData?.find(
        (inv: any) => inv.id === invoiceId
      );
      if(selectedInvoice){
        this.maxRefund = true;
      }

      const payments = selectedInvoice?.paymentInfos || [];
      const totalPaid = payments.reduce((sum: number, p: any) => {
        return sum + (p.paymentData?.amount || 0);
      }, 0);

      this.maxRefundAmount = totalPaid;

      // Re-apply validator
      const amountControl = this.refundForm.get('amount');
      amountControl?.setValidators([
        Validators.required,
        this.maxAmountValidator(this.maxRefundAmount),
      ]);
      amountControl?.updateValueAndValidity();
    });
  }

  initForm() {
    if (this.refundData) {
      this.refundForm = this.fb.group({
        invoiceId: [this.refundData?.invoiceId, Validators.required],
        customerId: [this.refundData?.customerId, Validators.required],
        amount: [this.refundData?.amount, Validators.required],
        currencyId: ['AED', Validators.required],
        refundDate: [this.refundData?.refundDate, Validators.required],
        paymentMethod: [this.refundData?.paymentMethod, Validators.required],
        accountId: [this.refundData?.accountId, Validators.required],
        referenceNumber: [
          this.refundData?.referenceNumber,
          Validators.required,
        ],
        notes: [this.refundData?.notes],
        chequeNo: [this.refundData?.chequeNo],
        chequeDate: [this.refundData?.chequeDate],
      });

      this.refundForm
        .get('customerId')
        ?.valueChanges.subscribe((customerId) => {
          if (customerId) {
            this.selectedCustomerId = customerId;
            this.fetchPaidInvoicesData(customerId, '');
          }
        });
    } else {
      this.refundForm = this.fb.group({
        invoiceId: [null, Validators.required],
        amount: [null, Validators.required],
        customerId: [null, Validators.required],
        currencyId: ['AED', Validators.required],
        refundDate: [null, Validators.required],
        paymentMethod: ['', Validators.required],
        accountId: [null, Validators.required],
        referenceNumber: ['', Validators.required],
        notes: [''],
        chequeNo: [''],
        chequeDate: [''],
      });
    }
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {

    // if (!this.refundForm.get('invoiceId')?.value) {
    //   this.toastr.warning('Please select an invoice first');
    //   event.preventDefault();
    //   return;
    // }
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onInvoiceAvailable(isOpened: boolean) {
    if (isOpened) {
      if (!this.selectedCustomerId) {
        this.toastr.warning(
          'Please select a customer first to choose an invoice'
        );
      } else {
        ('');
      }
    }
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onSearchInvoice(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.invoiceSearchSubject.next(searchTerm);
  }

  getCustomers(search: string = '') {
    this.invoiceService.fetchCustomers(search).subscribe({
      next: (data) => {
        this.customers = data;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  fetchrefundById() {
    this.refundService.fetchRefundById(Number(this.refundId)).subscribe({
      next: (data) => {
        this.refundData = data;
        this.initForm();
        this.selectedCustomerId = this.refundData?.customerId;
        this.fetchPaidInvoicesData(this.selectedCustomerId, '');
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  fetchPaidInvoicesData(customerId: any, search: string = '') {
    this.refundService.listPaidInvoices(customerId, search).subscribe({
      next: (invoiceData) => {
        this.paidInvoiceData = invoiceData;
        // if (invoiceData?.length === 0) {
        //   this.toastr.warning('This customer has no invoices for refund');
        // }
      },
      error: (error) => console.error(error),
    });
  }

  fetchAccounts() {
    this.refundService.fetchAccounts('').subscribe({
      next: (accounts) => {
        this.accountsData = accounts;
      },
      error: (error) => console.error(error),
    });
  }

  maxAmountValidator = (maxAmount: number) => {
    return (control: any) => {
      if (control.value != null && control.value > maxAmount) {
        return { maxAmount: true };
      }
      return null;
    };
  };

  private loadPaymentMethods() {
    this.paymentMethodData = Object.values(PaymentMethodsEnum).map((type) => ({
      label: type.label,
      value: type.value,
    }));
  }

  async onSubmit() {
    if (this.refundForm.invalid) {
      this.refundForm.markAllAsTouched();
      return;
    }
    const formValue = this.refundForm.value;

    const createRefundInput = {
      invoiceId: Number(formValue.invoiceId),
      customerId: Number(formValue.customerId),
      amount: parseFloat(formValue.amount),
      refundDate: formValue.refundDate,
      paymentMethod: formValue.paymentMethod,
      accountId: Number(formValue.accountId),
      referenceNumber: formValue.referenceNumber,
      notes: formValue.notes,
      isCheque: formValue.paymentMethod === 'Cheque' ? true : false,
      chequeNo:
        formValue.paymentMethod === 'Cheque' ? formValue.chequeNo || '' : null,
      chequeDate:
        formValue.paymentMethod === 'Cheque'
          ? formValue.chequeDate || null
          : null,
      refundStatus: 'Pending',
    };

    const updateRefundInput = {
      id: Number(this.refundId),
      invoiceId: Number(formValue.invoiceId),
      customerId: Number(formValue.customerId),
      amount: parseFloat(formValue.amount),
      refundDate: formValue.refundDate,
      paymentMethod: formValue.paymentMethod,
      accountId: Number(formValue.accountId),
      referenceNumber: formValue.referenceNumber,
      notes: formValue.notes,
      isCheque: formValue.paymentMethod === 'Cheque' ? true : false,
      chequeNo:
        formValue.paymentMethod === 'Cheque' ? formValue.chequeNo || '' : null,
      chequeDate:
        formValue.paymentMethod === 'Cheque'
          ? formValue.chequeDate || null
          : null,
      refundStatus: 'Pending',
    };
    if (this.refundId) {
      this.refundService.modifyRefund(updateRefundInput).subscribe({
        next: async (data) => {
          this.toastr.success('Refund updated successfully', 'Success');
          this.onCancel();
          this.router.navigate([`/refund`]);
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    } else {
      this.refundService.initiateRefund(createRefundInput).subscribe({
        next: async (data) => {
          this.toastr.success('Refund initiated successfully', 'Success');
          this.onCancel();
          this.router.navigate([`/refund`]);
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    }
  }

  onCancel() {
    this.router.navigate([`/refund`]);
  }
}
